import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material'
import { FC, useState } from 'react'
import { styled } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { updatePassword } from '../../services/AuthService'
import { ButtonCustom } from '../Button/ButtonCustom'
import { appTheme } from '../../theme'
import { flexItemCenter } from '../../global'
import PersonIcon from '../../images/User/PersonIcon.svg'

const useStyles = makeStyles({
  textField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderRadius: '8px'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333333'
      }
    },
    '& .css-15ubayx-MuiFormLabel-root-MuiInputLabel-root.Mui-focused': {
      color: '#333333'
    }
  }
})

interface NewPasswordProps {
  open: boolean
  onClose: () => void
  user: {
    id: number
    firstName: string
    lastName: string
    role: string
    subRole: string
    approverLevel: number
  }
  onSuccess?: () => void
}

const getRoleLabel = (role: string, subRole: string, approverLevel: number) => {
  if (role === 'ADMIN') return 'Admin'
  if (role === 'EDITOR') return 'Editor'
  if (role === 'REQUESTER') return 'User'
  return (
    `Approver ${approverLevel}` +
    (subRole
      ? ` - ${subRole.charAt(0)}${subRole.substring(1).toLowerCase()}`
      : '')
  )
}

export const NewPassword: FC<NewPasswordProps> = ({
  open,
  onClose,
  user,
  onSuccess
}) => {
  const classes = useStyles()
  const fullScreen = useMediaQuery(appTheme.breakpoints.down('md'))
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  const handleChange = (type: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'newPassword') {
      setNewPassword(e.target.value)
      setNewPasswordError('')
    } else if (type === 'confirmNewPassword') {
      setConfirmNewPassword(e.target.value)
      setConfirmNewPasswordError('')
    }
  }

  const handleUpdatePassword = async () => {
    setNewPasswordError('')
    setConfirmNewPasswordError('')

    if (!newPassword) return setNewPasswordError('Password cannot be empty')
    if (confirmNewPassword !== newPassword)
      return setConfirmNewPasswordError('Passwords do not match')

    setIsUpdating(true)

    try {
      const response = await updatePassword(user.id, newPassword)
      if (response.data.updatePassword.statusCode === 200) {
        setIsUpdating(false)
        onClose()
        setNewPassword('')
        setConfirmNewPassword('')
        onSuccess?.()
      }
    } catch (error) {
      console.error('Error updating password:', error)
      setIsUpdating(false)
    }
  }

  const handleClose = () => {
    if (!isUpdating) {
      onClose()
      setNewPassword('')
      setConfirmNewPassword('')
      setNewPasswordError('')
      setConfirmNewPasswordError('')
    }
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '20px 80px',
          gap: '16px'
        }
      }}
    >
      <DialogTitle style={flexItemCenter}>
        <Box
          sx={{ mr: '25px' }}
          component='img'
          src={PersonIcon}
          alt={PersonIcon}
        />
        <Box>
          <Typography color='rgba(0, 0, 0, 0.87)'>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography sx={{ fontSize: 10 }} color='rgba(0, 0, 0, 0.6)'>
            {getRoleLabel(user.role, user.subRole, user.approverLevel)}
          </Typography>
        </Box>
      </DialogTitle>
      <Box sx={{ width: { sm: 385 }, gap: '10px' }}>
        <TextField
          size='small'
          label='New Password'
          defaultValue='Type password'
          type={showPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={handleChange('newPassword')}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPassword(!showPassword)}
                  edge='end'
                  size="large">
                  {showPassword ? (
                    <VisibilityOff fontSize='small' />
                  ) : (
                    <Visibility fontSize='small' />
                  )}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%',
            '.MuiOutlinedInput-root.Mui-focused': {
              fieldset: { borderColor: '#006BD7' }
            },
            [`& fieldset`]: {
              borderRadius: '8px'
            }
          }}
          classes={{
            root: classes.textField
          }}
          helperText={newPasswordError}
          error={!!newPasswordError}
        />
        <TextField
          size='small'
          label='Confirm New Password'
          classes={{
            root: classes.textField
          }}
          defaultValue='Type password'
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmNewPassword}
          onChange={handleChange('confirmNewPassword')}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  edge='end'
                  size="large">
                  {showConfirmPassword ? (
                    <VisibilityOff fontSize='small' />
                  ) : (
                    <Visibility fontSize='small' />
                  )}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%',
            '.MuiOutlinedInput-root.Mui-focused': {
              fieldset: { borderColor: '#006BD7' }
            },
            [`& fieldset`]: {
              borderRadius: '8px'
            }
          }}
          helperText={confirmNewPasswordError}
          error={!!confirmNewPasswordError}
        />
      </Box>

      <Box style={flexItemCenter}>
        <ButtonCustom
          variant='outlined'
          icon={false}
          onClick={handleClose}
          label='Back'
        />
        <ButtonCustom
          icon={false}
          backgroundColor='#333333'
          color='#ffffff'
          onClick={handleUpdatePassword}
          label='Save'
          disabled={isUpdating}
        />
      </Box>
    </Dialog>
  )
}
